import { useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { apiFetch, post } from "@/lib/api";
import { authFetch } from "@/lib/fetch";
import { API_BASE } from "@/lib/utils";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { useAuth } from "@/lib/auth";
import { useInjectOrchestratorMessage, useOrchestratorSessions, type OrchestratorTodo } from "@/hooks/use-orchestrator";
import type { AttachmentMeta } from "@/hooks/use-orchestrator-attachments";
import type { SetupProduct } from "@/hooks/use-setup-journey";
import type { ConfigurationProposalData } from "@/components/PlannerInterruptPanel";
import { normalizeHitlQuestions, openHitl } from "@/pages/orchestrator/open-hitl";
import { aggregateSubagents, findPendingSubagentInterrupt } from "@/pages/orchestrator/useSubagentRuns";

/** One question the agent asked with options (present_user_options). */
export interface AgentQuestion {
  question: string;
  header: string;
  multiSelect?: boolean;
  options: Array<{ label: string; description: string }>;
}

/** The account the agent asks the person to connect so it can test against it (request_user_connection). */
export interface ConnectRequest {
  connectorId: string;
  connectorSlug: string;
  connectorName: string;
  authTypes?: string[];
  message?: string;
}

/**
 * What the agent is waiting on: questions, a configuration to review or a
 * connection to make here, or something only its own page can do (test case
 * or field mapping review, credentials). Read by the agent page's own rule,
 * so a question it shows is one this shows, and from a paused sub-agent too.
 */
export function pendingAsk(messages: UIMessage[]): { questions: AgentQuestion[]; handOff: boolean; connect: ConnectRequest | null; proposal: ConfigurationProposalData | null } {
  const none = { questions: [], handOff: false, connect: null, proposal: null };
  const sub = findPendingSubagentInterrupt(aggregateSubagents(messages));
  const open = openHitl(messages) ?? (sub ? { toolName: sub.toolName, output: sub.envelope } : null);
  if (!open) return none;
  const { toolName, output } = open;
  if (toolName === "present_user_options") {
    const questions = normalizeHitlQuestions(output);
    return questions.length > 0 ? { ...none, questions } : none;
  }
  if (toolName === "request_user_connection") {
    const c = output as Partial<ConnectRequest>;
    const connect = c.connectorId && c.connectorSlug && c.connectorName
      ? { connectorId: c.connectorId, connectorSlug: c.connectorSlug, connectorName: c.connectorName, authTypes: c.authTypes, message: c.message }
      : null;
    return { ...none, connect };
  }
  if (toolName === "propose_configuration" && Array.isArray(output["directions"])) {
    return { ...none, handOff: true, proposal: output as unknown as ConfigurationProposalData };
  }
  return { ...none, handOff: true };
}

/**
 * Whether the saved thread holds more than this tab's copy: more messages, or
 * the same ones with more of the last one. Never fewer, so a save that has not
 * landed yet cannot take back a message just sent.
 */
export function savedIsAhead(saved: readonly UIMessage[], local: readonly UIMessage[]): boolean {
  if (saved.length !== local.length) return saved.length > local.length;
  const s = saved[saved.length - 1];
  const l = local[local.length - 1];
  return !!s && !!l && s.role === l.role && s.parts.length > l.parts.length;
}

/** A message's text parts, joined. */
export function messageText(m: UIMessage): string {
  return (m.parts as Array<{ type: string; text?: string }>)
    .filter((p) => p.type === "text" && p.text)
    .map((p) => p.text!.trim())
    .join("\n")
    .trim();
}

/** The agent's last words, for the chat's latest message. */
export function lastAgentText(messages: UIMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.role !== "assistant") continue;
    const text = messageText(m);
    if (text) return text;
  }
  return null;
}

/**
 * The conversation as the chat shows it: each message's text, in order. The
 * first user message is the build prompt the screen sent, not something the
 * person typed, so it is left out, as is anything with no text (tool steps).
 */
export function chatTranscript(messages: readonly UIMessage[]): Array<{ id: string; role: "user" | "assistant"; text: string }> {
  const kickoff = messages.findIndex((m) => m.role === "user");
  const out: Array<{ id: string; role: "user" | "assistant"; text: string }> = [];
  messages.forEach((m, i) => {
    if (i === kickoff || (m.role !== "user" && m.role !== "assistant")) return;
    const text = messageText(m);
    if (text) out.push({ id: m.id, role: m.role, text });
  });
  return out;
}

/**
 * Whether the build has nothing to read: no docs link and no OpenAPI file,
 * while the data moves through an API. Started like that, the agent can only
 * stop and ask for them, and the build screen sits on "working" for hours.
 * Database or SFTP access is built from credentials, so it needs no docs.
 */
export function missingApiDocs(product: Pick<SetupProduct, "apiDocsUrl" | "openApiFile" | "direction">): boolean {
  return !product.apiDocsUrl?.trim() && !product.openApiFile && product.direction !== "access";
}

/**
 * Whether the agent ended its turn with work left and nothing to answer
 * inline: it asked in plain words, or stopped. Nothing runs until the person
 * replies, so the screen must say so rather than count the minutes up.
 */
export function waitingOnReply(
  messages: readonly UIMessage[],
  state: { running: boolean; todos: readonly OrchestratorTodo[]; asking: boolean },
): boolean {
  if (state.running || state.asking || state.todos.length === 0) return false;
  if (state.todos.every((t) => t.status === "completed")) return false;
  return messages[messages.length - 1]?.role === "assistant";
}

/**
 * The todo the agent gave up on: it marked it blocked and stopped, with no
 * question left to answer here. Nothing moves until the person changes what
 * it was given (a dead docs link, a product with no API), so the screen must
 * offer a way out rather than count the minutes up.
 */
export function blockedTodo(todos: readonly OrchestratorTodo[], state: { running: boolean; asking: boolean }): OrchestratorTodo | null {
  if (state.running || state.asking) return null;
  return todos.find((t) => t.status === "blocked") ?? null;
}

/**
 * The thread without the turn a rejoin replays. The run's buffer holds that
 * turn from its first event and the replay lands on the last message, so a
 * copy of the turn left in place would get every part twice.
 */
export function beforeTurnInFlight(messages: UIMessage[]): UIMessage[] {
  const i = messages.findLastIndex((m) => m.role === "user");
  return i < 0 || i === messages.length - 1 ? messages : messages.slice(0, i + 1);
}

/** How long to wait before rejoining a run the server still has going: doubling from 2s, capped at 30s. */
export function rejoinDelay(attempt: number): number {
  return Math.min(2_000 * 2 ** attempt, 30_000);
}

/**
 * The todo list to show: the stream's while it is newer than the last read of
 * the session row, else the row. The run writes the row before it streams the
 * list, so a read after the stream's last update is never behind it, and a
 * stream that closed early no longer pins the list where it closed.
 */
export function freshestTodos(
  live: { items: OrchestratorTodo[]; at: number } | null,
  saved: OrchestratorTodo[] | undefined,
  savedAt: number,
): OrchestratorTodo[] {
  if (live && (!saved || live.at > savedAt)) return live.items;
  return saved ?? [];
}

/** The connector a finished todo links to (`/connectors/<id>`), which is the product connector once step 1 is built. */
export function connectorIdFromTodos(todos: OrchestratorTodo[]): string | null {
  for (const t of todos) {
    const m = t.link?.href.match(/\/connectors\/([^/?#]+)/);
    if (m) return decodeURIComponent(m[1]!);
  }
  return null;
}

/**
 * Runs one agent thread for the onboarding: rejoins a turn already in flight,
 * mirrors its todo list as progress, and exposes the question it is waiting
 * on so the screen can answer it inline. The run itself is server-side, so it
 * keeps going when the screen is left or minimized.
 */
export function useAgentRun(threadId: string | null, initialMessages: UIMessage[] | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const [liveTodos, setLiveTodos] = useState<{ items: OrchestratorTodo[]; at: number } | null>(null);
  // Whether the server has a turn in flight, which outlives this tab's copy of
  // the stream: a rejoin answers 204 when nothing runs, and the run's stream
  // opens with data-run-started and ends with data-run-ended.
  const [serverLive, setServerLive] = useState(false);
  const rejoins = useRef(0);
  const [rejoinTick, setRejoinTick] = useState(0);
  const trimForReplay = useRef<() => void>(() => undefined);
  // Every request this hook opens, so leaving the screen can close them: the
  // SDK's rejoin (GET .../stream) is sent without an abort signal of its own.
  const open = useRef(new Set<AbortController>());

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${API_BASE}/v1/agent/orchestrator/stream`,
        fetch: (async (url, init) => {
          const ctl = new AbortController();
          const outer = init?.signal;
          if (outer) outer.addEventListener("abort", () => ctl.abort(), { once: true });
          open.current.add(ctl);
          try {
            const res = await authFetch(String(url), { ...init, signal: ctl.signal });
            if (init?.method === "GET") {
              if (res.status === 204) setServerLive(false);
              else if (res.ok) trimForReplay.current();
            }
            return res;
          } catch (err) {
            open.current.delete(ctl);
            throw err;
          }
        }) as typeof fetch,
        body: { threadId },
        prepareReconnectToStreamRequest: () => ({
          api: `${API_BASE}/v1/agent/orchestrator/sessions/${threadId}/stream`,
        }),
      }),
    [threadId],
  );

  const chat = useChat({
    id: threadId ?? "onboarding-idle",
    messages: initialMessages ?? [],
    transport,
    onFinish: () => {
      void queryClient.invalidateQueries({ queryKey: keys.orchestrator.sessions(orgId) });
      void queryClient.invalidateQueries({ queryKey: keys.orchestrator.messages(orgId, threadId ?? undefined) });
    },
    onData: (part) => {
      const p = part as { type?: string; data?: { items?: OrchestratorTodo[] } };
      if (p.type === "data-todos" && Array.isArray(p.data?.items)) setLiveTodos({ items: p.data.items, at: Date.now() });
      if (p.type === "data-run-started") setServerLive(true);
      if (p.type === "data-run-ended") { setServerLive(false); rejoins.current = 0; }
    },
  });

  // Rejoin a turn already running (a reload, or coming back from Minimize).
  const resumed = useRef<string | null>(null);
  useEffect(() => {
    if (!threadId || initialMessages === undefined || resumed.current === threadId) return;
    resumed.current = threadId;
    // useChat takes `messages` only when it creates the chat, which is before
    // the saved history has loaded; without this the screen never sees a
    // question the agent asked before a reload.
    if (chat.messages.length === 0 && initialMessages.length > 0) chat.setMessages(initialMessages);
    void chat.resumeStream().catch(() => undefined);
  }, [threadId, initialMessages, chat]);

  // Leaving the screen closes this tab's copy of the stream (the run goes on
  // server-side). Left open, each Open/Minimize held one more connection until
  // the run ended, and at the browser's six per host every other request queued.
  useEffect(() => {
    const requests = open.current;
    return () => {
      for (const ctl of requests) ctl.abort();
      requests.clear();
    };
  }, [threadId]);

  useEffect(() => {
    trimForReplay.current = () => chat.setMessages(beforeTurnInFlight);
  }, [chat]);

  const streaming = chat.status === "streaming" || chat.status === "submitted";
  const running = streaming || serverLive;
  // The session row is the record once the turn ends; the live list leads while it streams.
  const sessions = useOrchestratorSessions({ enabled: !!threadId, poll: running ? 4_000 : 15_000 });
  const session = sessions.data?.find((s) => s.threadId === threadId);

  // The server still runs the turn but this tab's stream closed before its end
  // (the network, a rejoin cut short): rejoin it, backing off, until it ends or
  // the server answers that nothing runs. The session poll keeps the todos
  // moving meanwhile.
  useEffect(() => {
    if (!threadId || streaming || !serverLive) return;
    const t = setTimeout(() => {
      rejoins.current += 1;
      void chat.resumeStream().catch(() => undefined).finally(() => setRejoinTick((n) => n + 1));
    }, rejoinDelay(rejoins.current));
    return () => clearTimeout(t);
  }, [threadId, streaming, serverLive, rejoinTick, chat]);

  // The saved thread wins once no turn streams here. A rejoined stream can
  // close before the run's last steps (a reload, Minimize, the network), and
  // this tab's copy then ends where it closed: without the question the agent
  // stopped on, so the screen said "the agent stopped" and showed nothing to
  // answer. The run saving bumps the session's updatedAt, which the sessions
  // poll sees, so the saved thread is re-read then and taken when it is ahead.
  const updatedAt = session?.updatedAt;
  useEffect(() => {
    if (!threadId || !updatedAt || running) return;
    void queryClient.invalidateQueries({ queryKey: keys.orchestrator.messages(orgId, threadId) });
  }, [threadId, orgId, updatedAt, running]);
  useEffect(() => {
    if (running || !initialMessages || !savedIsAhead(initialMessages, chat.messages)) return;
    chat.setMessages(initialMessages);
  }, [initialMessages, running, chat]);

  // The run calls the platform with the token it was started with, and access
  // tokens live 5 minutes while a build takes far longer: without a fresh one
  // every tool call after that fails as "token rejected". Same cadence as /agent,
  // and once on rejoin, since the token held by a resumed run may already be stale.
  useEffect(() => {
    if (!threadId || !running) return;
    const refresh = () => { void apiFetch(`${API_BASE}/v1/agent/orchestrator/sessions/${threadId}/token`, post({})).catch(() => undefined); };
    refresh();
    const t = setInterval(refresh, 45_000);
    return () => clearInterval(t);
  }, [threadId, running]);

  // A reply sent while a turn runs is steered in through /inject, as /agent
  // does: a second /stream on the same thread would interleave two turns. The
  // stream never echoes a user message, so it is shown from here until the
  // run ends and the saved thread, which holds it, replaces this tab's copy.
  const inject = useInjectOrchestratorMessage();
  const [queued, setQueued] = useState<UIMessage[]>([]);
  const wasRunning = useRef(running);
  useEffect(() => {
    const ended = wasRunning.current && !running;
    wasRunning.current = running;
    if (!ended || !threadId || queued.length === 0) return;
    void apiFetch<{ messages: UIMessage[] }>(`${API_BASE}/v1/agent/orchestrator/sessions/${threadId}/messages`)
      .then(({ messages }) => {
        chat.setMessages(messages);
        setQueued([]);
        queryClient.setQueryData(keys.orchestrator.messages(orgId, threadId), messages);
      })
      .catch(() => undefined);
  }, [running, threadId, queued.length, chat, orgId]);

  const send = async (text: string, attachment?: AttachmentMeta | null) => {
    const parts = [
      ...(attachment ? [{ type: "data-attachment" as const, data: attachment }] : []),
      { type: "text" as const, text },
    ];
    if (!running || !threadId) return chat.sendMessage({ parts });
    const message: UIMessage = { id: crypto.randomUUID(), role: "user", parts };
    setQueued((q) => [...q, message]);
    // The run can end between the last status this tab saw and the POST, and
    // /inject then answers queued: false. Send it as a turn of its own instead.
    const res = await inject.mutateAsync({ threadId, message }).catch(() => null);
    if (res && !res.queued) {
      setQueued((q) => q.filter((m) => m.id !== message.id));
      return chat.sendMessage({ parts });
    }
  };

  const todos = freshestTodos(liveTodos, session?.todos, sessions.dataUpdatedAt);
  const ask = pendingAsk(chat.messages);
  const done = !running && todos.length > 0 && todos.every((t) => t.status === "completed");
  const asking = ask.questions.length > 0 || ask.handOff || !!ask.connect;
  const waiting = waitingOnReply(chat.messages, { running, todos, asking });
  const blocked = blockedTodo(todos, { running, asking });

  return {
    /** When the thread started, for "Running for N min". */
    startedAt: session?.createdAt ?? null,
    todos,
    running,
    done,
    /** The turn ended with todos open and no question to answer here: the agent waits on a reply in the chat. */
    waiting,
    /** The todo the agent stopped on as blocked, when nothing else is waiting on the person. */
    blocked,
    questions: ask.questions,
    handOff: ask.handOff,
    connect: ask.connect,
    proposal: ask.proposal,
    lastText: lastAgentText(chat.messages),
    messages: chat.messages,
    /** Replies sent mid-turn, waiting for the agent's next step to read them. */
    queued,
    error: chat.error ?? inject.error,
    send,
  };
}

/** The ask for step 1: build the product's connector from what the person confirmed. */
export function productBuildPrompt(product: SetupProduct, opts: { small: boolean; productName: string; domain: string | null }): string {
  const lines = [
    `Connect my own product to fastn, so my customers' connections to it can be scoped. This is step 1 of my fastn setup.`,
    `Product: ${opts.productName}${opts.domain ? ` (${opts.domain})` : ""}.`,
    product.description ? `What it does: ${product.description}.` : null,
    product.apiDocsUrl
      ? `API docs: ${product.apiDocsUrl}`
      : product.openApiFile
        ? `The OpenAPI file ${product.openApiFile.filename} is attached.`
        : product.noApi
          ? `There is no public API yet.`
          : `There are no public API docs.`,
    product.direction === "access"
      ? "fastn should reach the product through database access or SFTP rather than an API."
      : product.direction ? `Data moves ${product.direction === "both" ? "both ways" : product.direction === "in" ? "into the product" : "out of the product"}.` : null,
    product.entities.length ? `Records my customers will sync: ${product.entities.join(", ")}.` : null,
    product.webhooks.length ? `Webhooks: ${product.webhooks.map((w) => w.name).join(", ")}.` : null,
    "",
    `Build a fastn connector for it: its sign-in method, then ${opts.small ? "two actions for the most important records now (the rest later)" : "actions for the records above"}, and test each action.`,
    `Keep a todo list with these items, in order: Read your API docs; Set up sign-in; Build actions; Test each action.`,
    `- Set up sign-in: the connector's sign-in method. It is done when the method exists; it does not need my account.`,
    `- Test each action: first ask me to connect a test account of ${opts.productName} with request_user_connection, and wait for me. Then run each action against that connection, read actions first, and ask before running any action that writes. It is done only when every action ran successfully against my account. Checking the request shape, mock data or a dry run is not a test. If I do not connect an account, leave it not done and tell me the actions are built but untested.`,
    `Ask me with options only when you must choose between real alternatives.`,
    `If you cannot go on without something only I can give (API docs, a spec, access), ask for it with present_user_options, never only in a message, and offer "I don't have it" as an option. If I pick that, mark the todo blocked, say plainly what is missing, and stop.`,
  ];
  return lines.filter((l): l is string => l !== null).join("\n");
}

/** The ask for step 2: build one integration from a picked use case, its parts kept as the build screen shows them. */
export function integrationBuildPrompt(
  useCase: string,
  connector: { name: string; id: string },
  product: { name: string; connectorId: string | null },
): string {
  return [
    `Build this integration for my product ${product.name}, for my customers to turn on in my widget: "${useCase}".`,
    product.connectorId
      ? `Both connectors already exist in fastn: ${connector.name} (id ${connector.id}) and ${product.name} (id ${product.connectorId}). Use them as they are. Do not create or rebuild either connector; if an action this needs is missing, add it to the existing connector.`
      : `The ${connector.name} connector already exists in fastn (id ${connector.id}). Use it as it is; do not create or rebuild it.`,
    `Before you build anything, ask me every question you need answered, all at once, in ONE present_user_options call (up to 4 questions, each with 2 to 3 options, the one you recommend first, and a short description on each option). After my answers, do not ask again unless you are truly blocked.`,
    `Keep a todo list with exactly these items, in this order, and do not rename them: Connector; Workflow; Trigger; Connection; UI widget.`,
    `Update the todo list as you go: set an item to in_progress before you start its work, and mark it completed before you start the next one. Never work on two items at once.`,
    `- Connector: check the actions this integration needs exist on the connectors. Note: the connector's name.`,
    `- Workflow: the workflow that does it, tested with sample data. Field mappings, the configuration proposal and the acceptance test cases are all part of Workflow. Note: its steps in a few words, joined with " → ".`,
    `- Trigger: what in ${product.name} starts it, a webhook event or a schedule. Note: it in a few words, such as "A new order in ${product.name}".`,
    `- Connection: each customer signs in to their own ${connector.name}; do not create a connection for them. To test, ask me to connect a test account with request_user_connection.`,
    product.connectorId
      ? `- UI widget: make the workflow available in my widget so customers turn it on there. Put both connectors on it and name it after ${connector.name}: ${product.name} is my product, so fastn keeps it off my customers' card and they only connect ${connector.name}.`
      : `- UI widget: make the workflow available in my widget so customers turn it on there. Name it after ${connector.name}.`,
    `Put each note in the todo's note field when you complete it. If the workflow needs a configuration (field mappings, matching), propose it with propose_configuration so I can review it.`,
  ].join("\n");
}

/**
 * Starts a turn on a thread and returns once the server has taken it. The run
 * lives on the server, so the response stream is closed here and the build
 * screen rejoins it with resumeStream, from this screen or after a reload.
 */
export async function startAgentRun(threadId: string, text: string, attachment?: AttachmentMeta | { attachmentId: string; filename: string } | null): Promise<void> {
  // The person picked "the agent does it for you", and these screens have no
  // approval card, so a gated tool (createConnector) would wait forever.
  await apiFetch(`${API_BASE}/v1/agent/orchestrator/sessions/${threadId}/auto-accept`, post({ enabled: true }));
  const controller = new AbortController();
  const message = {
    id: crypto.randomUUID(),
    role: "user",
    parts: [
      ...(attachment ? [{ type: "data-attachment", data: { mediaType: "text/plain", width: null, height: null, ...attachment } }] : []),
      { type: "text", text },
    ],
  };
  const res = await authFetch(`${API_BASE}/v1/agent/orchestrator/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ threadId, messages: [message] }),
    signal: controller.signal,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw Object.assign(new Error(body.error ?? `The agent could not start (${res.status})`), { status: res.status });
  }
  controller.abort();
}
